import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';

type ReviewItem = {
  id: string;
  rating: number;
  content: string | null;
  created_at: string;
  reviewer_id: string;
  reviewee_id: string;
  match_id: string;
  reviewer?: { name: string; role?: string } | null;
  reviewee?: { name: string; role?: string } | null;
  match?: {
    match_number: string | null;
    start_date: string | null;
    end_date: string | null;
    location: string | null;
  } | null;
};

type TabKey = 'received' | 'written';

export default function AppMyReviewsPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [activeTab, setActiveTab] = useState<TabKey>('received');
  const [receivedReviews, setReceivedReviews] = useState<ReviewItem[]>([]);
  const [writtenReviews, setWrittenReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auth.userId) return;
    loadAllReviews();
  }, [auth.userId]);

  async function loadAllReviews() {
    if (!auth.userId) return;
    setLoading(true);
    setError('');

    try {
      // 받은 리뷰 (내가 reviewee)
      const { data: received, error: rErr } = await supabase
        .from('reviews')
        .select('id, rating, content, created_at, reviewer_id, reviewee_id, match_id')
        .eq('reviewee_id', auth.userId)
        .order('created_at', { ascending: false });

      if (rErr) throw rErr;

      // 작성한 리뷰 (내가 reviewer)
      const { data: written, error: wErr } = await supabase
        .from('reviews')
        .select('id, rating, content, created_at, reviewer_id, reviewee_id, match_id')
        .eq('reviewer_id', auth.userId)
        .order('created_at', { ascending: false });

      if (wErr) throw wErr;

      // 프로필 + 매칭 정보 일괄 enrich
      const allUserIds = new Set<string>();
      const allMatchIds = new Set<string>();
      [...(received || []), ...(written || [])].forEach((r) => {
        allUserIds.add(r.reviewer_id);
        allUserIds.add(r.reviewee_id);
        allMatchIds.add(r.match_id);
      });
      allUserIds.delete(auth.userId);

      const [profileResult, matchResult] = await Promise.all([
        allUserIds.size > 0
          ? supabase.from('profiles').select('id, name, roles, role').in('id', Array.from(allUserIds))
          : Promise.resolve({ data: [], error: null }),
        allMatchIds.size > 0
          ? supabase.from('match_requests').select('id, match_number, start_date, end_date, location')
            .in('id', Array.from(allMatchIds))
          : Promise.resolve({ data: [], error: null }),
      ]);

      const profileMap = new Map<string, { name: string; role?: string }>();
      (profileResult.data || []).forEach((p) => {
        const rolesArr: string[] = (p as { roles?: string[] }).roles?.length
          ? (p as { roles: string[] }).roles
          : (p as { role?: string }).role ? [(p as { role: string }).role] : [];
        profileMap.set(p.id, {
          name: p.name,
          role: rolesArr.includes('caregiver') ? '간병인' : '환자/보호자',
        });
      });

      const matchMap = new Map<string, ReviewItem['match']>();
      (matchResult.data || []).forEach((m) => {
        matchMap.set(m.id, {
          match_number: m.match_number,
          start_date: m.start_date,
          end_date: m.end_date,
          location: m.location,
        });
      });

      function enrich(items: typeof received): ReviewItem[] {
        return (items || []).map((r) => ({
          ...r,
          reviewer: profileMap.get(r.reviewer_id) || null,
          reviewee: profileMap.get(r.reviewee_id) || null,
          match: matchMap.get(r.match_id) || null,
        }));
      }

      setReceivedReviews(enrich(received));
      setWrittenReviews(enrich(written));
    } catch (err) {
      console.error('Failed to load reviews:', err);
      setError('리뷰를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  const currentReviews = activeTab === 'received' ? receivedReviews : writtenReviews;

  const avgRating =
    currentReviews.length > 0
      ? Math.round((currentReviews.reduce((sum, r) => sum + r.rating, 0) / currentReviews.length) * 10) / 10
      : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => {
    const count = currentReviews.filter((r) => r.rating === star).length;
    const pct = currentReviews.length > 0 ? Math.round((count / currentReviews.length) * 100) : 0;
    return { star, count, pct };
  });

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center gap-3 px-5 pt-6 pb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">내 리뷰</h1>
            <p className="text-xs text-gray-400">작성한 리뷰와 받은 리뷰를 확인하세요</p>
          </div>
        </div>

        {/* 에러 */}
        {error && (
          <div className="mx-5 mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">
            {error}
            <button
              onClick={loadAllReviews}
              className="ml-2 underline cursor-pointer hover:text-red-600 font-medium"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* 탭 */}
        <div className="px-5 mb-4">
          <div className="bg-gray-100 rounded-full p-1 flex">
            <button
              onClick={() => setActiveTab('received')}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === 'received'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${activeTab === 'received' ? 'ri-star-fill text-amber-400' : 'ri-star-line'} mr-1.5`} />
              받은 리뷰 {receivedReviews.length > 0 && `(${receivedReviews.length})`}
            </button>
            <button
              onClick={() => setActiveTab('written')}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === 'written'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${activeTab === 'written' ? 'ri-edit-2-fill text-[#2E7D6B]' : 'ri-edit-2-line'} mr-1.5`} />
              작성한 리뷰 {writtenReviews.length > 0 && `(${writtenReviews.length})`}
            </button>
          </div>
        </div>

        {/* 리뷰 통계 카드 */}
        {!loading && currentReviews.length > 0 && (
          <div className="px-5 mb-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-amber-50 flex flex-col items-center justify-center shrink-0">
                  <span className="text-2xl font-extrabold text-gray-900 leading-none">{avgRating}</span>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i
                        key={s}
                        className={`text-[8px] ${
                          s <= Math.round(avgRating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {activeTab === 'received' ? '전체' : '내가 작성한'} {currentReviews.length}개 리뷰
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">평균 {avgRating}점</p>
                </div>
              </div>

              {/* 별점 분포 막대 */}
              <div className="space-y-1.5">
                {ratingDistribution.map((item) => (
                  <div key={item.star} className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 w-8 text-right shrink-0 whitespace-nowrap">
                      {item.star}점
                    </span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all duration-500"
                        style={{ width: `${item.pct}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-400 w-8 shrink-0 whitespace-nowrap">
                      {item.count}개
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 리뷰 리스트 */}
        {!loading && currentReviews.length === 0 ? (
          <div className="text-center py-20 px-5">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <i className={`text-2xl text-gray-300 ${activeTab === 'received' ? 'ri-chat-3-line' : 'ri-edit-2-line'}`} />
            </div>
            <p className="text-sm text-gray-400 font-medium">
              {activeTab === 'received' ? '아직 받은 리뷰가 없어요' : '아직 작성한 리뷰가 없어요'}
            </p>
            <p className="text-xs text-gray-300 mt-1">
              {activeTab === 'received'
                ? '매칭이 완료되면 상대방이 리뷰를 남길 수 있습니다'
                : '완료된 매칭에 대해 리뷰를 남겨보세요'}
            </p>
          </div>
        ) : (
          <div className="px-5 space-y-3">
            {currentReviews.map((r) => {
              const isReceived = activeTab === 'received';
              const personName = isReceived
                ? (r.reviewer?.name || '익명')
                : (r.reviewee?.name || '알 수 없음');
              const personRole = isReceived
                ? (r.reviewer?.role || '')
                : (r.reviewee?.role || '');
              const iconBg = isReceived ? 'bg-[#E3F2FD]' : 'bg-[#E8F5E9]';
              const iconColor = isReceived ? 'text-[#1976D2]' : 'text-[#2E7D6B]';
              const dateLabel = isReceived ? '' : '작성일';

              return (
                <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
                      <i className={`ri-user-3-line ${iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-gray-900">{personName}</span>
                        {personRole && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                            {personRole}
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400 ml-auto whitespace-nowrap">
                          {dateLabel}{' '}
                          {new Date(r.created_at).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>

                      {/* 매칭 정보 */}
                      {r.match && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-1.5">
                          {r.match.match_number && (
                            <span className="text-[11px] text-gray-400">
                              #{r.match.match_number}
                            </span>
                          )}
                          {r.match.start_date && (
                            <span className="text-[11px] text-gray-400">
                              {new Date(r.match.start_date).toLocaleDateString('ko-KR')}
                              {r.match.end_date && r.match.end_date !== r.match.start_date
                                ? ` ~ ${new Date(r.match.end_date).toLocaleDateString('ko-KR')}`
                                : ''}
                            </span>
                          )}
                          {r.match.location && (
                            <span className="text-[11px] text-gray-400 truncate max-w-[180px]">
                              {r.match.location}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-0.5 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i
                            key={star}
                            className={`text-xs ${
                              star <= r.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      {r.content ? (
                        <p className="text-sm text-gray-600 leading-relaxed">{r.content}</p>
                      ) : (
                        <p className="text-xs text-gray-400 italic">내용 없이 별점만 남긴 리뷰입니다</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}